/*
 * product-card.js — shared card interactions (delegated, works for every .cc-card).
 * Swatch hover/click: select that color's variant → swap the card image (data-image, from the
 * variant's custom.variant_images), update the card link + price + ship + active swatch.
 * Wishlist heart is handled by wishlist.js.
 */
(function () {
  'use strict';

  // Wishlist page locks each card to its saved variant (rendered server-side via ?variant=epi)
  // — block USER-driven colour switching here.
  function isLocked(sw) { return !!(sw.closest && sw.closest('.wishlist-grid')); }

  function selectSwatch(sw) {
    if (!sw) return;
    var url = sw.getAttribute('data-url');
    var card = sw.closest('.cc-card');
    if (card && url) {
      card.querySelectorAll('.cc-card__image-link, .cc-card__title-link').forEach(function (a) {
        a.setAttribute('href', url);
      });
      if (card.tagName === 'A') card.setAttribute('href', url);
    }
    // Swap the card image to the selected colour's variant image (sticky — stays on mouseleave).
    var img = sw.getAttribute('data-image');
    if (card && img) {
      // Specifically target .cc-card__image.is-main or .cc-card__image
      var imgEl = card.querySelector('.cc-card__image.is-main, .cc-card__image');
      if (imgEl && imgEl.tagName === 'IMG') {
        imgEl.removeAttribute('srcset');
        imgEl.srcset = '';
        imgEl.removeAttribute('sizes');
        imgEl.removeAttribute('loading');
        imgEl.setAttribute('src', img);
        imgEl.src = img;
      }
    }
    // Price + compare follow the selected variant (per-seat, computed server-side).
    if (card) {
      var price = sw.getAttribute('data-price');
      if (price) { var pEl = card.querySelector('.cc-card__price'); if (pEl) pEl.textContent = price; }
      var cEl = card.querySelector('.cc-card__compare-price');
      if (cEl && sw.hasAttribute('data-compare')) cEl.textContent = sw.getAttribute('data-compare') || '';
      // Ship time follows the selected colour's variant (custom.ship_time).
      var ship = sw.getAttribute('data-ship');
      var shipsEl = card.querySelector('.cc-card__ships');
      if (shipsEl && sw.hasAttribute('data-ship')) {
        var tEl = shipsEl.querySelector('.cc-card__ship-text');
        if (tEl) tEl.textContent = ship || '';
        shipsEl.style.display = ship ? '' : 'none';
      }
    }
    var group = sw.parentNode;
    if (group) {
      group.querySelectorAll('.cc-card__swatch--active').forEach(function (s) {
        s.classList.remove('cc-card__swatch--active');
      });
    }
    sw.classList.add('cc-card__swatch--active');

    if (card) {
      var heart = card.querySelector('.js-wishlist-btn');
      var variantId = sw.getAttribute('data-variant-id');
      if (heart && variantId) {
        heart.setAttribute('data-epi', variantId);
        heart.dispatchEvent(new CustomEvent('product-card:variant-change', {
          bubbles: true,
          detail: { variantId: variantId }
        }));
      }
    }
  }

  // Preload all variant images for a product card on first hover/mouseover over .cc-card
  function preloadCardImages(card, fetchPriority) {
    if (!card || card.dataset.preloaded) return;
    card.dataset.preloaded = 'true';
    var swatches = card.querySelectorAll('.cc-card__swatch[data-image]');
    var seen = {};
    swatches.forEach(function (sw) {
      var imgUrl = sw.getAttribute('data-image');
      if (imgUrl && !seen[imgUrl]) {
        seen[imgUrl] = true;
        var img = new Image();
        if (fetchPriority && 'fetchPriority' in img) img.fetchPriority = fetchPriority;
        img.decoding = 'async';
        img.src = imgUrl;
      }
    });
  }

  // Touch screens have no reliable hover phase before a swatch tap. Warm a card's
  // variant-image cache just before it enters view instead of attaching a scroll
  // handler that runs for every frame. The narrow root margin limits this to the
  // next row of cards; slow/data-saving connections retain interaction-only loading.
  var mobileCardPreloadObserver;
  function canPreloadMobileCards() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return false;
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !(connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || '')));
  }

  function observeMobileCardPreloads() {
    if (!canPreloadMobileCards() || !('IntersectionObserver' in window)) return;
    if (!mobileCardPreloadObserver) {
      mobileCardPreloadObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          mobileCardPreloadObserver.unobserve(entry.target);
          preloadCardImages(entry.target, 'low');
        });
      }, { rootMargin: '0px 0px 200px 0px' });
    }
    document.querySelectorAll('.cc-card').forEach(function (card) {
      if (card.dataset.preloaded || card.dataset.mobilePreloadObserved) return;
      card.dataset.mobilePreloadObserved = 'true';
      mobileCardPreloadObserver.observe(card);
    });
  }

  // Preload variant images as soon as user hovers anywhere on the card
  ['mouseover', 'pointerover'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var card = e.target.closest ? e.target.closest('.cc-card') : null;
      if (card) preloadCardImages(card);
    });
  });

  // Hover selects the variant (active + card link follow the hovered swatch).
  ['mouseover', 'pointerover', 'mouseenter'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var sw = e.target.closest ? e.target.closest('.cc-card__swatch') : null;
      if (!sw || isLocked(sw)) return;
      selectSwatch(sw);
    });
  });

  // Click on a swatch selects the variant instead of navigating (touch / no-hover).
  document.addEventListener('click', function (e) {
    var sw = e.target.closest ? e.target.closest('.cc-card__swatch') : null;
    if (!sw || isLocked(sw)) return;
    e.preventDefault();
    e.stopPropagation();
    selectSwatch(sw);
  });

  // Keyboard a11y (swatches are role="button" tabindex="0").
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var sw = e.target.closest ? e.target.closest('.cc-card__swatch[data-variant-id]') : null;
    if (!sw || isLocked(sw)) return;
    e.preventDefault();
    selectSwatch(sw);
  });

  // ---- Swatch overflow: show as many swatches as fit one row; rest -> "+N" (computed, not a fixed limit) ----
  function layoutSwatchOverflow(container) {
    var swatches = Array.prototype.slice.call(container.querySelectorAll('.cc-card__swatch'));
    if (!swatches.length) return;
    var chip = container.querySelector('.cc-card__swatch-more');
    swatches.forEach(function (s) { s.style.display = ''; });
    if (chip) chip.style.display = 'none';
    var cs = window.getComputedStyle(container);
    var rightEdge = container.getBoundingClientRect().right - (parseFloat(cs.paddingRight) || 0);
    var fit = 0;
    for (var i = 0; i < swatches.length; i++) {
      if (swatches[i].getBoundingClientRect().right <= rightEdge + 0.5) fit++;
      else break;
    }
    if (fit >= swatches.length) return;        // all fit, no chip
    if (!chip) return;
    var visible = fit;
    for (var k = visible; k < swatches.length; k++) swatches[k].style.display = 'none';
    var hidden = swatches.length - visible;
    chip.style.display = '';
    chip.textContent = '+' + hidden;
    // Reserve room for the chip: hide more swatches until the chip itself fits on the row.
    var guard = 0;
    while (visible > 0 && chip.getBoundingClientRect().right > rightEdge + 0.5 && guard < 30) {
      visible--;
      swatches[visible].style.display = 'none';
      hidden++;
      chip.textContent = '+' + hidden;
      guard++;
    }
  }

  function layoutAllSwatches() {
    document.querySelectorAll('.cc-card__swatches').forEach(layoutSwatchOverflow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestAnimationFrame(function () {
        layoutAllSwatches();
        observeMobileCardPreloads();
      });
    });
  } else {
    requestAnimationFrame(function () {
      layoutAllSwatches();
      observeMobileCardPreloads();
    });
  }
  window.addEventListener('load', function () {
    layoutAllSwatches();
    observeMobileCardPreloads();
  });
  var _swRT;
  window.addEventListener('resize', function () {
    clearTimeout(_swRT);
    _swRT = setTimeout(layoutAllSwatches, 150);
  });

  // Async sections inject their cards AFTER load/DOMContentLoaded have fired, so
  // those swatch rows never get trimmed and overflow. Watch the known async card
  // hosts and re-run the layout when cards land:
  //   <product-recommendations>   — Shopify Recommendations API (innerHTML swap)
  //   [data-facet-grid]            — collection/search page (cgrid-facets AJAX filter updates)
  //   #wishlist-items-container    — wishlist page (per-item Section Rendering fetch,
  //                                  cards appended into nested slots → needs subtree)
  // The rAF flag coalesces the burst of mutations into a single layout pass.
  // (layoutSwatchOverflow only touches inline `style`, not childList, so it can't
  //  retrigger the observer → no loop.)
  var _swPending = false;
  function scheduleSwatchLayout() {
    if (_swPending) return;
    _swPending = true;
    requestAnimationFrame(function () {
      _swPending = false;
      layoutAllSwatches();
      observeMobileCardPreloads();
    });
  }
  document.querySelectorAll('product-recommendations, [data-facet-grid], #wishlist-items-container').forEach(function (el) {
    new MutationObserver(scheduleSwatchLayout).observe(el, { childList: true, subtree: true });
  });
})();
